/**
 * One meal, as the trainer reads it: what was asked for, and what came back.
 *
 * The header carries the whole story for a trainer scanning the day — the tri-state mark, and macros
 * that are the **actual** totals rather than the planned ones. That choice matters: a header showing
 * the plan would read as adherence on a meal nobody touched. The plan is on the rows, next to the
 * actual, which is where a comparison belongs.
 *
 * Read-only throughout. The builder's `MealCard` is the editable twin and shares nothing but tokens:
 * threading a `readOnly` prop through its `NumberInput`s, serving `Select`s, reorder and delete
 * controls would be a larger change than this whole file, and none of the five things a log row shows
 * exists there.
 */

import Box from "@mui/material/Box";
import { MacroLine } from "../ui/MacroChips";
import { deviationValue, energyFigure } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { v2 } from "../ui/v2Tokens";
import {
  actualMealTotals,
  mealDeviationKcal,
  mealEatenState,
  mealRowViews,
  type DayLogV2,
} from "../model/dayLogV2Model";
import type { MealV2 } from "../model/nutritionV2Model";
import { isNoteVisible } from "../model/nutritionV2Model";
import { EatenMark } from "./EatenMark";
import { LogFoodRow } from "./LogFoodRow";

const nums = { unicodeBidi: "isolate", direction: "ltr" as const, display: "inline-block" };

export const LogMealCard = ({
  meal,
  log,
  /** this meal's share of the day's calories, already computed by the day view */
  sharePct,
}: {
  meal: MealV2;
  log: DayLogV2;
  sharePct?: number;
}) => {
  const rows = mealRowViews(meal, log);
  const totals = actualMealTotals(meal, log);
  const state = mealEatenState(meal, log);
  const mode = useNutritionDisplayMode();
  const drift = mealDeviationKcal(meal, log);
  // the drift as it is shown — a few kcal round to 0 points, and a "0 נק׳" line says nothing
  const shownDrift = deviationValue(drift, mode);
  const calories = energyFigure(totals.calories, mode, { short: true });
  const driftFigure = energyFigure(Math.abs(drift), mode, { short: true });
  const note = isNoteVisible(meal) ? meal.notes : undefined;
  const time = meal.timeFrom && meal.timeTo ? `${meal.timeFrom}–${meal.timeTo}` : "";

  return (
    <Box
      data-testid="log-meal-card"
      sx={{
        backgroundColor: v2.surface,
        border: `1px solid ${v2.line}`,
        borderRadius: "14px",
        p: 1.5,
        mb: 1.25,
      }}
    >
      {/* Addressable so a test can assert the HEADER's numbers without matching the rows beneath,
          which repeat the same figures for individual foods. */}
      <Box
        data-testid="log-meal-header"
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <EatenMark state={state} />
        <Box sx={{ fontSize: 14, fontWeight: 700, color: v2.ink }}>{meal.name}</Box>
        {/* A blank time range is hidden rather than rendered as a dash — plenty of meals have none. */}
        {time ? <Box sx={{ fontSize: 11.5, color: v2.ink4, ...nums }}>{time}</Box> : null}

        <Box sx={{ flex: 1 }} />

        {typeof sharePct === "number" && sharePct > 0 && (
          <Box
            sx={{
              fontSize: 10.5,
              fontWeight: 600,
              color: v2.ink3,
              backgroundColor: v2.lineSoft,
              px: 0.75,
              py: 0.15,
              borderRadius: "6px",
            }}
          >
            <Box component="span" sx={nums}>
              {sharePct}%
            </Box>{" "}
            מהיום
          </Box>
        )}
        <MacroLine totals={totals} size={11} />
        <Box sx={{ fontSize: 13, fontWeight: 700, color: v2.ink, ...nums }}>
          {`${calories.text} ${calories.unit}`}
        </Box>
      </Box>

      {shownDrift !== 0 && (
        <Box
          data-testid="log-meal-drift"
          sx={{
            mt: 0.5,
            fontSize: 11.5,
            fontWeight: 600,
            color: shownDrift > 0 ? v2.over : v2.under,
          }}
        >
          <Box component="span" sx={nums}>
            {driftFigure.text}
          </Box>{" "}
          {`${driftFigure.unit} ${shownDrift > 0 ? "מעל המתוכנן" : "מתחת למתוכנן"}`}
        </Box>
      )}

      {note ? (
        <Box
          sx={{
            mt: 1,
            px: 1.25,
            py: 0.75,
            fontSize: 12,
            color: v2.ink2,
            backgroundColor: v2.canvas,
            borderRadius: "8px",
          }}
        >
          {note}
        </Box>
      ) : null}

      <Box sx={{ mt: 1 }}>
        {rows.length ? (
          rows.map((row) => <LogFoodRow key={`${row.item.uid}-${row.source}`} row={row} />)
        ) : (
          <Box sx={{ fontSize: 12, color: v2.ink4, px: 1.25, py: 1 }}>אין מאכלים בארוחה הזו</Box>
        )}
      </Box>
    </Box>
  );
};
